import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { admin } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Pencil, Users, RotateCcw, DollarSign, History, CheckCircle, XCircle, Clock, Trash2, Ban, KeyRound, Eye } from 'lucide-react';

const AdminUsers = () => {
  usePageTitle('Manage Users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('approved');
  const [formData, setFormData] = useState({
    credit_limit: '',
    is_active: true
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await admin.getUsers({ limit: 100 });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Approve user
  const handleApprove = async (user) => {
    try {
      await admin.updateUser(user.id, { is_approved: true });
      toast.success('User approved successfully! An email notification has been sent.');
      loadUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  // Reject user (delete their account)
  const handleReject = async (user) => {
    if (!window.confirm(`Are you sure you want to reject ${user.company_name || user.email}? This will delete their pending account.`)) {
      return;
    }
    
    try {
      await admin.deleteUser(user.id);
      toast.success('User rejected and removed from pending list');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  // Disable/Enable user
  const handleToggleActive = async (user) => {
    const newStatus = !user.is_active;
    try {
      await admin.updateUser(user.id, { is_active: newStatus });
      toast.success(newStatus ? 'User enabled successfully' : 'User disabled successfully');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  // Delete user
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${user.company_name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await admin.deleteUser(user.id);
      toast.success(`${user.company_name} has been deleted`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Reset user password
  const handleResetPassword = async (user) => {
    if (!window.confirm(`Reset password for ${user.company_name || user.email}? A temporary password will be generated.`)) {
      return;
    }
    
    try {
      const response = await admin.resetUserPassword(user.id);
      const { temporary_password, user_email } = response.data;
      
      // Show the temporary password in a dialog
      toast.success(
        <div>
          <p className="font-semibold">Password Reset!</p>
          <p className="text-sm mt-1">Email: {user_email}</p>
          <p className="text-sm">Temp Password: <span className="font-mono bg-gray-100 px-1">{temporary_password}</span></p>
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      credit_limit: user.credit_limit?.toString() || '5000',
      is_active: user.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleViewDetails = (user) => {
    setEditingUser(user);
    setDetailsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await admin.updateUser(editingUser.id, {
        credit_limit: parseFloat(formData.credit_limit),
        is_active: formData.is_active
      });
      
      toast.success('User updated successfully');
      setDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  // Reset credit (Option 1)
  const handleResetCredit = async (user, resetAll = true) => {
    const amount = resetAll ? null : parseFloat(prompt(`Enter amount to reduce from credit used ($${user.credit_used?.toFixed(2) || 0}):`));
    
    if (!resetAll && (isNaN(amount) || amount <= 0)) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await admin.resetUserCredit(user.id, amount);
      toast.success(resetAll ? 'Credit reset successfully' : `Credit reduced by $${amount.toFixed(2)}`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to reset credit');
    }
  };

  // Record manual payment (Option 4)
  const handleOpenPaymentDialog = (user) => {
    setEditingUser(user);
    setPaymentData({
      amount: user.credit_used?.toString() || '',
      payment_method: 'bank_transfer',
      reference: '',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await admin.recordPayment(editingUser.id, {
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        reference: paymentData.reference || null,
        notes: paymentData.notes || null
      });
      
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // View payment history
  const handleViewPaymentHistory = async (user) => {
    setEditingUser(user);
    try {
      const response = await admin.getUserPayments(user.id);
      setPaymentHistory(response.data.payments);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load payment history');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-white" />
        ))}
      </div>
    );
  }

  const pendingUsers = users.filter(u => !u.is_approved);
  const approvedUsers = users.filter(u => u.is_approved !== false);

  return (
    <div className="space-y-6 fade-in" data-testid="admin-users">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A]">Customers</h1>
        <p className="text-[#64748B]">{users.length} registered buyers</p>
      </div>

      {/* Pending Approvals Alert */}
      {pendingUsers.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">
              <strong>{pendingUsers.length}</strong> customer(s) pending approval
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setActiveTab('pending')}
            className="border-yellow-400 text-yellow-700 hover:bg-yellow-100"
          >
            Review Now
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle className="w-4 h-4" />
            Approved ({approvedUsers.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="w-4 h-4" />
            Pending ({pendingUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* Approved Users Tab */}
        <TabsContent value="approved">
          <div className="bg-white border border-[#E2E8F0] overflow-hidden">
            {approvedUsers.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
                <p className="text-[#64748B]">No approved customers yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-[#64748B]">Company</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748B]">Contact</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748B]">Credit Limit</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748B]">Credit Used</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748B]">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748B]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {approvedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-[#F8FAFC]" data-testid={`user-row-${user.id}`}>
                      <td className="p-4">
                        <p className="font-medium text-[#0F172A]">{user.company_name}</p>
                        <p className="text-xs text-[#64748B]">{user.email}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-[#0F172A]">{user.contact_name}</p>
                        <p className="text-xs text-[#64748B]">{user.phone || 'No phone'}</p>
                      </td>
                      <td className="p-4 font-semibold">${(user.credit_limit || 5000).toFixed(2)}</td>
                      <td className="p-4">
                        <span className={user.credit_used > 0 ? 'text-orange-600 font-semibold' : 'text-green-600'}>
                          ${(user.credit_used || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="p-4">
                        <Badge className={user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {user.is_active !== false ? 'Active' : 'Disabled'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(user)}
                            title="View Details"
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            title="Edit User"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {user.credit_used > 0 && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetCredit(user, true)}
                                title="Reset All Credit"
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`reset-credit-${user.id}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenPaymentDialog(user)}
                                title="Record Payment"
                                className="text-green-600 hover:text-green-700"
                                data-testid={`record-payment-${user.id}`}
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewPaymentHistory(user)}
                            title="Payment History"
                            className="text-purple-600 hover:text-purple-700"
                            data-testid={`payment-history-${user.id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetPassword(user)}
                            title="Reset Password"
                            className="text-blue-500 hover:text-blue-600"
                            data-testid={`reset-password-${user.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(user)}
                            title={user.is_active !== false ? 'Disable User' : 'Enable User'}
                            className={user.is_active !== false ? 'text-orange-500 hover:text-orange-600' : 'text-green-500 hover:text-green-600'}
                            data-testid={`toggle-active-${user.id}`}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            title="Delete User"
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Pending Users Tab */}
        <TabsContent value="pending">
          <div className="bg-white border border-[#E2E8F0] overflow-hidden">
            {pendingUsers.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-200 mx-auto mb-4" />
                <p className="text-[#64748B]">No pending approvals</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E2E8F0]">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="p-6 hover:bg-[#F8FAFC]" data-testid={`pending-user-${user.id}`}>
                    <div className="flex justify-between items-start">
                      {/* Left: User Details */}
                      <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-[#0F172A]">{user.company_name}</h3>
                          <Badge className="bg-yellow-100 text-yellow-800">Pending Approval</Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-[#64748B] text-xs uppercase tracking-wide">Contact Name</p>
                            <p className="text-[#0F172A] font-medium">{user.contact_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-[#64748B] text-xs uppercase tracking-wide">Email</p>
                            <p className="text-[#0F172A] font-medium">{user.email}</p>
                          </div>
                          <div>
                            <p className="text-[#64748B] text-xs uppercase tracking-wide">Phone</p>
                            <p className="text-[#0F172A] font-medium">{user.phone || '-'}</p>
                          </div>
                          <div>
                            <p className="text-[#64748B] text-xs uppercase tracking-wide">ABN</p>
                            <p className="text-[#0F172A] font-medium font-mono">{user.abn || '-'}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-[#64748B] text-xs uppercase tracking-wide">Business Address</p>
                            <p className="text-[#0F172A] font-medium">{user.address || '-'}</p>
                          </div>
                        </div>
                        
                        <p className="text-xs text-[#94A3B8]">
                          Registered: {user.created_at ? new Date(user.created_at).toLocaleDateString() + ' at ' + new Date(user.created_at).toLocaleTimeString() : '-'}
                        </p>
                      </div>
                      
                      {/* Right: Action Buttons */}
                      <div className="flex flex-col gap-2 ml-6">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(user)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-user-${user.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(user)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`reject-user-${user.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <p className="text-sm text-[#64748B] mb-4">
                  Editing: <strong>{editingUser.company_name}</strong>
                </p>
              </div>

              <div>
                <Label htmlFor="credit_limit">Credit Limit ($)</Label>
                <Input
                  id="credit_limit"
                  type="number"
                  min="0"
                  step="100"
                  value={formData.credit_limit}
                  onChange={(e) => setFormData(prev => ({ ...prev, credit_limit: e.target.value }))}
                  className="mt-1"
                  data-testid="credit-limit-input"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F8FAFC]">
                <div>
                  <Label htmlFor="is_active">Account Status</Label>
                  <p className="text-xs text-[#64748B]">
                    {formData.is_active ? 'Account is active' : 'Account is disabled'}
                  </p>
                </div>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  data-testid="is-active-switch"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#0025a0]" data-testid="save-user-btn">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0]">
                <p className="text-sm text-[#64748B]">Customer: <strong>{editingUser.company_name}</strong></p>
                <p className="text-sm text-[#64748B]">Outstanding Balance: <strong className="text-orange-600">${(editingUser.credit_used || 0).toFixed(2)}</strong></p>
              </div>

              <div>
                <Label htmlFor="payment_amount">Payment Amount ($) *</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                  className="mt-1"
                  required
                  data-testid="payment-amount-input"
                />
              </div>

              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select
                  value={paymentData.payment_method}
                  onValueChange={(value) => setPaymentData(prev => ({ ...prev, payment_method: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="payment_reference">Reference / Transaction ID</Label>
                <Input
                  id="payment_reference"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="e.g., TXN-12345"
                  className="mt-1"
                  data-testid="payment-reference-input"
                />
              </div>

              <div>
                <Label htmlFor="payment_notes">Notes</Label>
                <Textarea
                  id="payment_notes"
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes about this payment"
                  rows={2}
                  className="mt-1"
                  data-testid="payment-notes-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="confirm-payment-btn">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment History</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div>
              <p className="text-sm text-[#64748B] mb-4">
                Customer: <strong>{editingUser.company_name}</strong>
              </p>

              {paymentHistory.length === 0 ? (
                <div className="p-8 text-center text-[#64748B]">
                  No payment history found
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-[#F8FAFC] sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium text-[#64748B]">Date</th>
                        <th className="text-left p-3 text-xs font-medium text-[#64748B]">Amount</th>
                        <th className="text-left p-3 text-xs font-medium text-[#64748B]">Method</th>
                        <th className="text-left p-3 text-xs font-medium text-[#64748B]">Reference</th>
                        <th className="text-left p-3 text-xs font-medium text-[#64748B]">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {paymentHistory.map((payment) => (
                        <tr key={payment.id}>
                          <td className="p-3 text-sm">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-sm font-semibold text-green-600">
                            ${payment.amount.toFixed(2)}
                          </td>
                          <td className="p-3 text-sm capitalize">
                            {payment.payment_method.replace('_', ' ')}
                          </td>
                          <td className="p-3 text-sm text-[#64748B] font-mono">
                            {payment.reference || '-'}
                          </td>
                          <td className="p-3">
                            <Badge className={payment.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {payment.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View User Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Company Name</p>
                  <p className="text-[#0F172A] font-semibold">{editingUser.company_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Contact Name</p>
                  <p className="text-[#0F172A] font-medium">{editingUser.contact_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Email</p>
                  <p className="text-[#0F172A] font-medium">{editingUser.email || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Phone</p>
                  <p className="text-[#0F172A] font-medium">{editingUser.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">ABN</p>
                  <p className="text-[#0F172A] font-medium font-mono">{editingUser.abn || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Status</p>
                  <Badge className={editingUser.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {editingUser.is_active !== false ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Business Address</p>
                  <p className="text-[#0F172A] font-medium">{editingUser.address || '-'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Credit Limit</p>
                  <p className="text-[#0F172A] font-semibold">${(editingUser.credit_limit || 5000).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Credit Used</p>
                  <p className={editingUser.credit_used > 0 ? 'text-orange-600 font-semibold' : 'text-green-600 font-semibold'}>
                    ${(editingUser.credit_used || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Registered</p>
                  <p className="text-[#0F172A] text-sm">
                    {editingUser.created_at ? new Date(editingUser.created_at).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Approval Status</p>
                  <Badge className={editingUser.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {editingUser.is_approved ? 'Approved' : 'Pending'}
                  </Badge>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
